import Papa from 'papaparse';

const REQUIRED_COLUMNS = ['client_id', 'cap', 'start_date', 'end_date'];
const DATE_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const generateRowId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export interface BatchImportRow {
  rowId: string;
  clientId: number;
  cap: number;
  startDate: string;
  endDate: string;
  status?: 'success' | 'error';
  error?: string | null;
}

export interface CsvParseResult {
  rows: BatchImportRow[];
  errors: string[];
}

const parsePositiveInteger = (value: string | undefined): number | null => {
  if (!value || value.trim() === '') return null;
  const parsed = Number(value.trim());
  if (isNaN(parsed) || !Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const isValidDateString = (value: string): boolean => {
  if (!DATE_FORMAT_REGEX.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const validateRow = (
  row: BatchImportRow,
): { valid: boolean; error: string | null } => {
  const errors: string[] = [];

  // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  if (!row.clientId || row.clientId <= 0) {
    errors.push('invalid client_id');
  }
  if (!row.cap || row.cap <= 0) {
    errors.push('invalid cap');
  }
  if (!row.startDate || !isValidDateString(row.startDate)) {
    errors.push('invalid start_date (expected YYYY-MM-DD)');
  }
  if (!row.endDate || !isValidDateString(row.endDate)) {
    errors.push('invalid end_date (expected YYYY-MM-DD)');
  }

  if (errors.length > 0) {
    return { valid: false, error: errors.join(', ') };
  }
  return { valid: true, error: null };
};

export const createEmptyRow = (): BatchImportRow => ({
  rowId: generateRowId(),
  clientId: 0,
  cap: 0,
  startDate: '',
  endDate: '',
});

const parseCsvContent = (csvText: string): CsvParseResult => {
  const results = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
  });

  const errors: string[] = [];

  if (!results.data.length) {
    return { rows: [], errors: ['CSV file is empty'] };
  }

  const headers = Object.keys(results.data[0]).map(h => h.trim().toLowerCase());
  const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));

  if (missingColumns.length > 0) {
    return {
      rows: [],
      errors: [`Missing required columns: ${missingColumns.join(', ')}`],
    };
  }

  const rows: BatchImportRow[] = results.data.map((record, index) => {
    const normalizedRecord: Record<string, string> = {};
    Object.entries(record).forEach(([key, value]) => {
      normalizedRecord[key.trim().toLowerCase()] = value;
    });

    const clientId = parsePositiveInteger(normalizedRecord['client_id']) ?? 0;
    const cap = parsePositiveInteger(normalizedRecord['cap']) ?? 0;
    const startDate = (normalizedRecord['start_date'] ?? '').trim();
    const endDate = (normalizedRecord['end_date'] ?? '').trim();

    if (clientId === 0) {
      errors.push(`Row ${index + 1}: invalid client_id`);
    }
    if (cap === 0) {
      errors.push(`Row ${index + 1}: invalid cap`);
    }
    if (startDate && !isValidDateString(startDate)) {
      errors.push(`Row ${index + 1}: invalid start_date (expected YYYY-MM-DD)`);
    }
    if (endDate && !isValidDateString(endDate)) {
      errors.push(`Row ${index + 1}: invalid end_date (expected YYYY-MM-DD)`);
    }

    return {
      rowId: generateRowId(),
      clientId,
      cap,
      startDate,
      endDate,
    };
  });

  return { rows, errors };
};

export const parseProgramCapCsv = (file: File): Promise<CsvParseResult> =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = (event): void => {
      const text = (event.target?.result as string) ?? '';
      resolve(parseCsvContent(text));
    };
    reader.onerror = (): void => {
      resolve({ rows: [], errors: ['Failed to read CSV file'] });
    };
    reader.readAsText(file);
  });

export const downloadCsvTemplate = (): void => {
  const csvContent =
    REQUIRED_COLUMNS.join(',') + '\n' + '12345,50000,2026-01-01,2026-12-31\n';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'program_caps_template.csv';
  link.click();
  URL.revokeObjectURL(url);
};
